import { Grid2 as Grid, TextField } from '@mui/material';
import { useState } from 'react';

import { updateAttachment, updateDownload } from '../../api/business/endpoints';
import type { ProductAttachment, ProductDownload } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { FormDialog } from '../../components/FormDialog';
import { LocalisedTextField } from '../../components/LocalisedTextField';
import { changedTexts, parseLimit, textsOf } from '../../components/productFiles';
import { useContentLanguages } from '../../components/useContentLanguages';
import { useLanguage } from '../../i18n/LanguageContext';

type ProductFileDialogProps = {
  companyId: string;
  productId: string;
  /** A download has limits, an attachment descriptions. */
  file: { kind: 'downloads'; item: ProductDownload } | { kind: 'attachments'; item: ProductAttachment };
  onClose: () => void;
};

/**
 * A download's names per language and its limits, or an attachment's names and descriptions (R0030 WP-10). Only what
 * changed is sent; an emptied text is cleared, and the file's own name shows in its place.
 */
export const ProductFileDialog = ({ companyId, productId, file, onClose }: ProductFileDialogProps) => {
  const { t } = useLanguage();
  const { languages, available } = useContentLanguages();
  const [names, setNames] = useState(() => textsOf(file.item.names, languages));
  const [descriptions, setDescriptions] = useState(() =>
    textsOf(file.kind === 'attachments' ? file.item.descriptions : {}, languages),
  );
  const [maxDownloads, setMaxDownloads] = useState(() =>
    file.kind === 'downloads' && file.item.maxDownloads !== null ? String(file.item.maxDownloads) : '',
  );
  const [validityDays, setValidityDays] = useState(() =>
    file.kind === 'downloads' && file.item.validityDays !== null ? String(file.item.validityDays) : '',
  );
  const [problem, setProblem] = useState<string | null>(null);
  const save = useAppMutation(
    () => {
      if (file.kind === 'attachments') {
        return updateAttachment(companyId, productId, file.item.id, {
          names: changedTexts(names, file.item.names),
          descriptions: changedTexts(descriptions, file.item.descriptions),
        });
      }

      return updateDownload(companyId, productId, file.item.id, {
        names: changedTexts(names, file.item.names),
        maxDownloads: parseLimit(maxDownloads, 1000000) ?? null,
        validityDays: parseLimit(validityDays, 3650) ?? null,
      });
    },
    ['product'],
    onClose,
  );

  const submit = (): void => {
    if (
      file.kind === 'downloads' &&
      (parseLimit(maxDownloads, 1000000) === undefined || parseLimit(validityDays, 3650) === undefined)
    ) {
      setProblem(t('files.limitInvalid'));

      return;
    }

    setProblem(null);
    save.mutate(undefined);
  };

  return (
    <FormDialog
      title={`${t('files.editTitle')} — ${file.item.file.fileName}`}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={t('common.save')}
      isBusy={save.isPending}
      error={save.error}
      problem={problem}
    >
      <LocalisedTextField
        label={t('files.name')}
        languages={languages}
        available={available}
        values={names}
        onChange={setNames}
        maxLength={255}
        autoFocus
      />
      {file.kind === 'attachments' ? (
        <LocalisedTextField
          label={t('files.description')}
          languages={languages}
          available={available}
          values={descriptions}
          onChange={setDescriptions}
          maxLength={1000}
        />
      ) : (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label={t('files.maxDownloads')}
              helperText={t('files.unlimitedHint')}
              value={maxDownloads}
              inputProps={{ inputMode: 'numeric' }}
              onChange={(e) => setMaxDownloads(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label={t('files.validityDays')}
              helperText={t('files.unlimitedHint')}
              value={validityDays}
              inputProps={{ inputMode: 'numeric' }}
              onChange={(e) => setValidityDays(e.target.value)}
            />
          </Grid>
        </Grid>
      )}
    </FormDialog>
  );
};
